import { States, SearchTaskRequestTaskDue, Stages } from '@urbanx/agx.activities.client';
import {
  AgxSelectChip,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
  Icon_MagnifyingGlass,
  SelectChipOption,
} from '@urbanx/agx.ui-components2';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AvailableStatesForTasks } from './AvailableStatesForTasks';
import { getTaskStageLabel } from 'utils/tasks';
import { AvailableStageCategories } from './AvailableTaskCategories';
import { isEqual } from 'lodash-es';
import { createInitialFilterState, storeFilterState, TaskFilterState } from './TaskFiltersState';
import './TaskFilters.scss';
import { useDebouncedState } from '@urbanx/agx-ui-components';

interface TaskFiltersProps {
  totalCount?: number;
  onFilterChange: (filterState: TaskFilterState) => void;
  pending: boolean;
  assigneeOptions: SelectChipOption[];
  agencyOptions: SelectChipOption[];
}

const TaskFilters = ({
  totalCount,
  onFilterChange,
  pending,
  assigneeOptions,
  agencyOptions,
}: TaskFiltersProps) => {
  const initialFilterState = createInitialFilterState();
  const [filters, setFilters] = useState<TaskFilterState>(initialFilterState);
  const [searchText, setSearchText] = useDebouncedState('', 300);

  const stateOptions = useMemo(() => {
    return AvailableStatesForTasks.map((state) => ({
      value: state,
      label: state,
      selected: filters.states?.includes(state) ?? false,
    }));
  }, [filters.states]);

  const stageOptions = useMemo(() => {
    return AvailableStageCategories.sort().map((stage) => {
      const value = stage as Stages;
      return {
        value,
        label: getTaskStageLabel(stage),
        selected: filters.stages?.includes(value) ?? false,
      };
    });
  }, [filters.stages]);

  const dueOptions = useMemo(() => {
    return [
      {
        value: SearchTaskRequestTaskDue.Today,
        label: 'Today',
        selected: filters.taskDue === SearchTaskRequestTaskDue.Today,
      },
      {
        value: SearchTaskRequestTaskDue.ThisWeek,
        label: 'This week',
        selected: filters.taskDue === SearchTaskRequestTaskDue.ThisWeek,
      },
      {
        value: SearchTaskRequestTaskDue.NextWeek,
        label: 'Next week',
        selected: filters.taskDue === SearchTaskRequestTaskDue.NextWeek,
      },
      {
        value: SearchTaskRequestTaskDue.Overdue,
        label: 'Overdue',
        selected: filters.taskDue === SearchTaskRequestTaskDue.Overdue,
      },
    ];
  }, [filters.taskDue]);

  const onStateFilterUpdate = useCallback(
    (selected: SelectChipOption[] = []) => {
      const selectedStateValue = selected.map((option) => option.value as States);

      if (!isEqual(selectedStateValue, filters.states)) {
        setFilters((prevFilters) => ({
          ...prevFilters,
          states: selectedStateValue,
        }));
      }
    },
    [filters.states]
  );

  const onAddressSearchTextFilterUpdate = useCallback(() => {
    if (!isEqual(searchText, filters.addressSearchText))
      setFilters((prevFilters) => ({
        ...prevFilters,
        addressSearchText: searchText,
      }));
  }, [filters.addressSearchText, searchText]);

  const handleAssigneeFilterUpdate = useCallback(
    (selected: SelectChipOption[] = []) => {
      const selectedAssignees = selected.map((option) => option.value as string);

      if (!isEqual(selectedAssignees, filters.assignedToUserIds))
        setFilters((prevFilters) => ({
          ...prevFilters,
          assignedToUserIds: selectedAssignees,
        }));
    },
    [filters.assignedToUserIds]
  );

  const handleAgencyFilterUpdate = useCallback(
    (selected: SelectChipOption[] = []) => {
      const selectedAgencyValue = selected.map((option) => option.value as string);

      if (!isEqual(selectedAgencyValue, filters.agencyIds))
        setFilters((prevFilters) => ({
          ...prevFilters,
          agencyIds: selectedAgencyValue,
        }));
    },
    [filters.agencyIds]
  );

  const handleTaskFilterUpdate = useCallback(
    (selected: SelectChipOption[] = []) => {
      const selectedTaskValue = selected.map((option) => option.value as Stages);

      if (!isEqual(selectedTaskValue, filters.stages))
        setFilters((prevFilters) => ({
          ...prevFilters,
          stages: selectedTaskValue,
        }));
    },
    [filters.stages]
  );

  const handleDueFilterUpdate = useCallback(
    (selected: SelectChipOption[] = []) => {
      const selectedDueValues = selected.map(
        (option) => option.value as SearchTaskRequestTaskDue
      )?.[0];

      if (!isEqual(selectedDueValues, filters.taskDue))
        setFilters((prevFilters) => ({
          ...prevFilters,
          taskDue: selectedDueValues,
        }));
    },
    [filters.taskDue]
  );

  useEffect(() => {
    onAddressSearchTextFilterUpdate();
  }, [searchText]);

  useEffect(() => {
    onFilterChange(filters);

    storeFilterState(filters);
  }, [filters]);

  if (pending) {
    return null;
  }

  return (
    <AgxSpacer
      className="taskFilters"
      direction="horizontal"
      padding="md"
      gap="sm"
      fill
      contentAlignment="center"
    >
      <AgxSpacer className="taskFiltersCount">
        <AgxTypography id="tasksNumber" type="body_ui" size="medium" style={{ flexShrink: 0 }}>
          <AgxTypography id="tasksNumber" type="body_ui" size="medium" color="var(--text-primary)">
            {`${totalCount ? `${totalCount} Tasks` : ''}`}
          </AgxTypography>
        </AgxTypography>
      </AgxSpacer>

      <AgxSpacer className="taskFiltersSearch">
        <AgxTextInput
          id={'address-search'}
          type={'text'}
          placeholder={'Search by address'}
          border="rounded"
          hideOptionalText
          leftIcon={
            <Icon_MagnifyingGlass color="var(--fg-tertiary, #B9B9B9)" height={15} width={15} />
          }
          onChange={(v) => setSearchText(v)}
          minWidth="320px"
          noHeader
        />
      </AgxSpacer>

      <AgxSpacer
        direction="horizontal"
        gap="sm"
        contentAlignment="center"
        contentJustification="end"
        className="taskFiltersControls"
        wrap
      >
        <AgxSelectChip
          id={'filterState'}
          variant="chip"
          label={'State'}
          onSelect={onStateFilterUpdate}
          onReset={() => onStateFilterUpdate([])}
          size={'medium'}
          type={'multi-select'}
          options={stateOptions}
          maxLabels={0}
          hideSelectAll
        />

        <AgxSelectChip
          id={'filterStage'}
          variant="chip"
          label={'Task'}
          onSelect={handleTaskFilterUpdate}
          onReset={() => handleTaskFilterUpdate([])}
          size={'medium'}
          type={'multi-select'}
          options={stageOptions}
          align="right"
          searchPlaceHolder="Search task type"
          maxLabels={0}
          hideSelectAll
        />

        <AgxSelectChip
          id={'filterAgency'}
          variant="chip"
          label={'Agency'}
          onSelect={handleAgencyFilterUpdate}
          onReset={() => handleAgencyFilterUpdate([])}
          size={'medium'}
          type={'multi-select'}
          searchPlaceHolder="Search agency"
          options={agencyOptions}
          maxLabels={0}
          hideSelectAll
        />

        <AgxSelectChip
          id={'filterAssignee'}
          variant="chip"
          label={'Assignee'}
          onSelect={handleAssigneeFilterUpdate}
          onReset={() => handleAssigneeFilterUpdate([])}
          size={'medium'}
          type={'multi-select'}
          options={assigneeOptions}
          searchPlaceHolder="Search Assignee"
          maxLabels={0}
          hideSelectAll
        />

        <AgxSelectChip
          id={'filterDue'}
          variant="chip"
          label={'Due'}
          onSelect={handleDueFilterUpdate}
          onReset={handleDueFilterUpdate}
          size={'medium'}
          type={'single-select'}
          options={dueOptions}
          align="right"
          maxLabels={0}
        />
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default TaskFilters;
